import * as React from 'react';
import {
    getCombinedSlideHeight,
    getInitialSlideHeight,
    getLargestNumberFromList
} from '../utils';
import {
    SLIDE_HEIGHT_MOBILE,
    SLIDE_HEIGHT_NON_MOBILE
} from '../components/SlideContent';
import {vhToPixels} from '../../../../utils/vhToPixels';

import {CSS_VAR_TOP_BAR_HEIGHT} from '../../navigation-top-bar-one-hub-helpers';
import {useDisclaimerHeights} from '../../../context/DisclaimerHeight';

/**
 *
 * @param ref
 * @param isMobile
 * @param heightOverride
 */
export function useActiveSlideHeight(
    ref: React.RefObject<HTMLDivElement>,
    isMobile: boolean,
    heightOverride: string | undefined
) {
    const {disclaimerHeights} = useDisclaimerHeights();

    const initialSlideHeight = React.useMemo(() => {
        const startingVH = getInitialSlideHeight(
            isMobile,
            SLIDE_HEIGHT_MOBILE,
            SLIDE_HEIGHT_NON_MOBILE
        );

        return vhToPixels(startingVH);
    }, [isMobile]);

    const activeSlideHeightRef = React.useRef<number>(initialSlideHeight);
    const isClientRef = React.useRef<boolean>(false);

    const setCSSHeightValue = React.useCallback(
        (height: number) => {
            const slideHeight = `${height}px`;

            return isMobile
                ? `calc(${slideHeight} + var(${CSS_VAR_TOP_BAR_HEIGHT}))`
                : slideHeight;
        },
        [isMobile]
    );

    const disclaimerHeight = React.useMemo(
        () =>
            isClientRef.current
                ? getLargestNumberFromList(disclaimerHeights)
                : 0,
        [disclaimerHeights]
    );

    const combinedSlideHeight = React.useMemo(() => {
        if (!isClientRef.current) return initialSlideHeight;

        return getCombinedSlideHeight({
            disclaimerHeight,
            heightOverride,
            initialSlideHeight
        });
    }, [disclaimerHeight, heightOverride, initialSlideHeight]);

    React.useEffect(() => {
        if (isClientRef.current) return;

        isClientRef.current = true;

        return () => {
            isClientRef.current = false;
        };
    }, [isClientRef]);

    React.useEffect(() => {
        if (!isClientRef.current) return;
        if (!ref.current) return;

        const height = setCSSHeightValue(combinedSlideHeight);
        const minHeight = initialSlideHeight + 'px';

        ref.current.style.height = height;
        ref.current.style.minHeight = minHeight;
    }, [
        ref,
        activeSlideHeightRef,
        combinedSlideHeight,
        initialSlideHeight,
        setCSSHeightValue
    ]);
}
